import { Link } from '../../router/Link';
import { Button, Chip, Notice, ThumbUpIcon, cx } from '../../ui';
import { RichText } from '../../richText/RichText';
import { useAutoLoadMore } from '../../listing/useAutoLoadMore';
import { SectionHead } from './SectionHead';
import type { CommentView, ThreadView } from './comments';
import type { CommentsBinding } from './useComments';
import styles from './comments.module.css';
import { useCopy } from '../../strings/useCopy';

/**
 * §8's comment section. Renders props: no fetching, no URL building, no core import — the hrefs arrived
 * resolved and the decisions were made in core or in `comments.ts` (R13).
 *
 * The comment body is the same `RichText` the description uses, which is the payoff of R8's node tree:
 * comments come from `contentHtml` and descriptions from the plain `description` (their HTML boundaries
 * differ in honesty), and by the time either reaches here that difference is gone and there
 * is one renderer.
 */

/**
 * The two marks in a comment's footer. They were the characters `♥` and `❤`, which is what the
 * bullet "make the heart the same size as the like icon" was about: those are two different codepoints
 * and most fonts draw the second noticeably larger, several platforms as a colour emoji. Drawn, they are
 * one hand and one size by construction — the 24-grid, `currentColor`, the same 14 px inside
 * `--vs-text-caption` — and the like count stops claiming to be a heart, which it never was: the API
 * field is `likeCount`.
 *
 * The **thumb** moved to `ui/icons.tsx` when the watch page's own like count took it: two
 * places counting the same field had to stop drawing it twice. The heart stays here, being the one
 * glyph in the app that means "the uploader hearted this".
 */
function HeartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={14}
      height={14}
      aria-hidden="true"
      focusable="false"
      className={styles.footIcon}
    >
      {/* Filled, because this one is a *badge* rather than a count: two arcs over a point. */}
      <path
        fill="currentColor"
        d="M12 20.4 4.6 13a4.7 4.7 0 0 1 6.6-6.7l.8.8.8-.8A4.7 4.7 0 0 1 19.4 13z"
      />
    </svg>
  );
}

function Avatar({ comment }: { comment: CommentView }) {
  // An avatar-less comment is a *normal* state: `authorThumbnails` is intermittently empty for a whole
  // page (§6). A gap would read as a rendering bug, so the initial stands in for it.
  if (comment.authorAvatarUrl === null) {
    return (
      <div className={cx(styles.avatar, styles.avatarBlank)} aria-hidden="true">
        {comment.authorInitial}
      </div>
    );
  }
  return (
    <img
      className={styles.avatar}
      src={comment.authorAvatarUrl}
      alt=""
      width={40}
      height={40}
      loading="lazy"
    />
  );
}

function CommentBody({ comment, onSeek }: { comment: CommentView; onSeek: (seconds: number) => void }) {
  const copy = useCopy().text.watch;
  const words = copy.comments;
  const pageWords = copy.page;
  return (
    <article className={styles.comment}>
      {comment.authorHref === null ? (
        <Avatar comment={comment} />
      ) : (
        <Link className={styles.avatarLink} href={comment.authorHref} ariaLabel={comment.author}>
          <Avatar comment={comment} />
        </Link>
      )}

      <div className={styles.body}>
        <div className={styles.head}>
          {comment.isPinned ? <span className={styles.badge}>{words.pinned}</span> : null}
          {comment.authorHref === null ? (
            <span className={cx(styles.author, comment.isChannelOwner && styles.owner)}>
              {comment.author}
            </span>
          ) : (
            <Link
              className={cx(styles.author, comment.isChannelOwner && styles.owner)}
              href={comment.authorHref}
            >
              {comment.author}
            </Link>
          )}
          {comment.verified ? (
            <span className={styles.verified} title={pageWords.verified}>
              ✓
            </span>
          ) : null}
          {comment.isSponsor ? (
            <span className={cx(styles.badge, styles.badgeMember)}>{words.member}</span>
          ) : null}
          {comment.publishedText === null ? null : (
            <span className={styles.time}>{comment.publishedText}</span>
          )}
          {comment.isEdited ? <span className={styles.time}>{words.edited}</span> : null}
        </div>

        <RichText className={styles.text} nodes={comment.bodyNodes} onSeek={onSeek} />

        <div className={styles.foot}>
          {comment.likeCountText === null ? null : (
            <span className={styles.footItem} title={comment.likeCountTitle ?? undefined}>
              {/* The glyph is decoration; the count needs a word beside it for anyone who cannot see
                  the thumb, and `srOnly` is that word rather than a `title` nothing announces. */}
              <ThumbUpIcon className={styles.footIcon} />
              <span className={styles.srOnly}>{words.likes} </span>
              {comment.likeCountText}
            </span>
          )}
          {comment.hearted ? (
            <span
              className={cx(styles.footItem, styles.heart)}
              title={
                comment.heartedBy === null
                  ? words.heartedByCreator
                  : words.heartedBy(comment.heartedBy)
              }
            >
              <HeartIcon />
              {/*
               * The channel, as its **avatar** rather than as its name. The handle beside
               * the heart was a second byline on a row that already has one three lines up, and on a
               * long channel name it wrapped the footer; the picture says the same thing in one glyph's
               * width. The sentence is now always spoken rather than only when the payload has no name,
               * because with the name gone from the screen `srOnly` is the *only* place it is said —
               * `alt=""` on the image for the same reason, so a reader hears one statement and not two.
               */}
              {comment.heartedByAvatarUrl === null ? null : (
                <img
                  className={styles.heartAvatar}
                  src={comment.heartedByAvatarUrl}
                  alt=""
                  width={16}
                  height={16}
                  loading="lazy"
                />
              )}
              <span className={styles.srOnly}>
                {comment.heartedBy === null ? words.heartedByCreator : words.heartedBy(comment.heartedBy)}
              </span>
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function Thread({
  thread,
  handlers,
  onSeek,
}: {
  thread: ThreadView;
  handlers: CommentsBinding['handlers'];
  onSeek: (seconds: number) => void;
}) {
  const id = thread.comment.id;
  const words = useCopy().text.watch.comments;

  return (
    <li className={styles.thread}>
      <CommentBody comment={thread.comment} onSeek={onSeek} />

      {thread.toggleLabel === null ? null : (
        <Button
          variant="ghost"
          size="sm"
          className={styles.repliesToggle}
          ariaExpanded={thread.expanded}
          onClick={() => handlers.toggleReplies(id)}
        >
          <span className={cx(styles.caret, thread.expanded && styles.caretOpen)} aria-hidden="true">
            ▸
          </span>
          {thread.toggleLabel}
        </Button>
      )}

      {thread.expanded ? (
        <div className={styles.replies}>
          {thread.replies.map((reply) => (
            <CommentBody key={reply.id} comment={reply} onSeek={onSeek} />
          ))}
          {thread.loading ? <Notice>{words.loadingReplies}</Notice> : null}
          {thread.moreLabel === null || thread.loading ? null : (
            <Button variant="subtle" onClick={() => handlers.loadMoreReplies(id)}>
              {thread.moreLabel}
            </Button>
          )}
        </div>
      ) : null}

      {thread.failed ? (
        <Notice tone="error" live>
          {words.repliesFailed}{' '}
          <Button variant="subtle" onClick={() => handlers.toggleReplies(id)}>
            {words.repliesRetry}
          </Button>
        </Notice>
      ) : null}
    </li>
  );
}

export function Comments({
  binding,
  onSeek,
  shown,
  onToggleShown,
  className,
}: {
  binding: CommentsBinding;
  /** A `t=` in a comment seeks *this* video rather than navigating — the same as in the description. */
  onSeek: (seconds: number) => void;
  /** The fold-away: hidden, the heading and its count stay and a Show button replaces the sort. */
  shown: boolean;
  onToggleShown: () => void;
  className?: string;
}) {
  const { view, handlers } = binding;
  const copy = useCopy().text;
  const words = copy.watch.comments;
  // The auto-load: the observer presses Show more as it scrolls near — the button stays, so
  // keyboards keep a control and the end of the thread list stays a visible sentence. The margin is
  // deliberately tight: comments are read top to bottom, so the next page is wanted when the
  // reader is close to the thread's end — the default viewport-early fetch grew the page under them.
  const autoLoad = useAutoLoadMore(shown && view.canLoadMore && !view.loadingMore, handlers.loadMore, {
    margin: '120px 0px',
  });

  return (
    <section className={cx(styles.comments, className)} aria-label={words.sectionLabel}>
      {/* The toggle takes the row's far end — the same edge every controls row keeps it on. And
          the whole head presses: the heading is a button, the row's empty space forwards to
          it, and a press on a sort chip is left alone (`SectionHead`). */}
      <SectionHead
        as="header"
        className={styles.header}
        headingClassName={styles.heading}
        heading={view.heading}
        shown={shown}
        onToggle={onToggleShown}
      >
        {!shown ? (
          <Button
            variant="subtle"
            className={styles.toggle}
            label={words.section.showLabel}
            ariaExpanded={false}
            onClick={onToggleShown}
          >
            {words.section.show}
          </Button>
        ) : view.phase === 'absent' ? null : (
          <div className={styles.sort} role="group" aria-label={words.sortLabel}>
            {view.sortOptions.map((option) => (
              <Chip key={option.id} selected={option.selected} onClick={() => handlers.setSort(option.id)}>
                {option.label}
              </Chip>
            ))}
            <Button
              variant="subtle"
              className={styles.toggle}
              label={words.section.hideLabel}
              ariaExpanded={true}
              onClick={onToggleShown}
            >
              {words.section.hide}
            </Button>
          </div>
        )}
      </SectionHead>

      {!shown ? null : view.notice === null ? null : (
        // A substituted sort or a truncated page is a *caveat about what you are looking at*, which is
        // exactly what the caution tone means (§6's `top` → `new` rescue).
        <Notice tone={view.notice.tone === 'warning' ? 'caution' : 'info'}>{view.notice.text}</Notice>
      )}

      {!shown ? null : view.phase === 'loading' ? (
        <Notice>{words.loading}</Notice>
      ) : view.failureText !== null ? (
        <Notice tone="error" live>
          {view.failureText}{' '}
          {view.retryable ? (
            <Button variant="subtle" onClick={handlers.retry}>
              {copy.shell.state.retry}
            </Button>
          ) : null}
        </Notice>
      ) : view.emptyText !== null ? (
        <Notice>{view.emptyText}</Notice>
      ) : (
        <>
          <ol className={styles.list}>
            {view.threads.map((thread) => (
              <Thread key={thread.comment.id} thread={thread} handlers={handlers} onSeek={onSeek} />
            ))}
          </ol>

          {view.loadMoreFailureText === null ? null : (
            <Notice tone="error" live>
              {view.loadMoreFailureText}
            </Notice>
          )}

          {view.canLoadMore ? (
            <div className={styles.moreRow} ref={autoLoad}>
              <Button className={styles.more} disabled={view.loadingMore} onClick={handlers.loadMore}>
                {view.loadingMore ? words.loadingMore : words.showMore}
              </Button>
            </div>
          ) : null}

        </>
      )}
    </section>
  );
}
